import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { Constants, Globals } from 'utils';
import { UserService } from 'services';
import { useBasics } from "../hooks/use-basics";

export const STEP = {
  STEP1: 1,
  STEP2: 2,
  STEP3: 3,
  STEP4: 4,
  STEP5: 5,
  STEP6: 6,
  STEP7: 7,
};

export const MODAL = {
  NONE: 0,
  SPECIALITY: 1,
  LANGUAGE: 2,
  ANIMAL: 3,
  SERVICE: 4,
  CATEGORY: 5,
  TERMS: 6,
};

export const useForm = () => {
  const nav = useHistory();
  const { form, getCities, modal, onChange, onChangeValue, selects, setModal, setStep, step } = useBasics(Constants.AFFILIATE_TYPES.CLINIC);

  const isValidForm = (showError = true) => {
    const onError = (msg: string) => {
      if (showError) Globals.showError(msg);
      return false;
    }

    if (step === STEP.STEP1) {
      if (!form.name)
        return onError('Debe indicar el nombre de la empresa');

      if (!form.document_type_id)
        return onError('Debe indicar el tipo de documento');

      if (!form.document)
        return onError('Debe indicar el documento de identidad');

      if (!form.birthdate)
        return onError('Debe indicar la fecha de fundado');

      if (!form.phonecode_id)
        return onError('Debe indicar el código de teléfono');

      if (!form.phone)
        return onError('Debe indicar el teléfono');

      if (!form.email)
        return onError('Debe indicar el correo electrónico');

      if (!String(form.email).match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/))
        return onError('Debe indicar un correo electrónico válido');

      if (!form.state_id)
        return onError('Debe indicar el estado');

      if (!form.city_id)
        return onError('Debe indicar la ciudad');

      if (!form.address)
        return onError('Debe indicar dirección');

      if (!form.contact_person_name)
        return onError('Debe indicar el nombre de la persona de contacto');

      if (!form.contact_person_doc_type_id)
        return onError('Debe indicar el tipo de documento de la persona de contacto');

      if (!form.contact_person_document)
        return onError('Debe indicar el documento de identidad de la persona de contacto');

      if (!form.contact_person_phone_code_id)
        return onError('Debe indicar el código de teléfono de la persona de contacto');

      if (!form.contact_person_phone)
        return onError('Debe indicar el teléfono de la persona de contacto');

      if (!form.contact_person_email)
        return onError('Debe indicar el correo electrónico de la persona de contacto');

      if (!String(form.contact_person_email).match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/))
        return onError('Debe indicar un correo electrónico válido para la persona de contacto');

      if (!form.password)
        return onError('Debe indicar la contraseña');

      if (String(form.password).length < 8)
        return onError('La contraseña debe tener al menos 8 caracteres');

      if (form.password !== form.password_confirmation)
        return onError('Las contraseñas no coinciden');

      if (!form.terms)
        return onError('Debe aceptar los términos y condiciones');
    }

    if (step === STEP.STEP2) {
      if (!form.languages.length)
        return onError('Debe indicar al menos un idioma');

      if (!form.areas_specialization.length)
        return onError('Debe indicar al menos un área de especialización');

      if (!form.animals_affiliate.length)
        return onError('Debe indicar al menos un animal');

      if (!form.services.length)
        return onError('Debe indicar al menos un servicio');

      if (!form.categories.length)
        return onError('Debe indicar al menos una categoría');
    }

    if (step === STEP.STEP3) {
      if (form.veterinarians.length) {
        const hasEmptyVet = form.veterinarians.some(i => !i.name || i.name.trim() === '');
        if (hasEmptyVet)
          return onError('Debe indicar el nombre de todos los veterinarios agregados');
      }
    }

    if (step === STEP.STEP4) {
      if (form.products.length) {
        const hasEmptyProductName = form.products.some(i => !i.name || i.name.trim() === '');
        if (hasEmptyProductName)
          return onError('Debe indicar el nombre de todos los productos agregados');
        const hasEmptyProductImage = form.products.some(i => !i.image && !i.image_blob);
        if (hasEmptyProductImage)
          return onError('Debe subir una foto para todos los productos agregados');
      }
    }

    if (step === STEP.STEP5) {
      if (!form.consultation.init_hour)
        return onError('Debe indicar la hora de inicio de consulta');

      if (!form.consultation.end_hour)
        return onError('Debe indicar la hora de fin de consulta');

      if (moment(form.consultation.init_hour, 'HH:mm:ss').isAfter(moment(form.consultation.end_hour, 'HH:mm:ss')))
        return onError('La hora de inicio no puede ser posterior a la hora de fin de consulta');

      if (!form.consultation.days.length)
        return onError('Debe indicar los días de consulta');

      if (!form.consultation.teleconsultation_rate_id)
        return onError('Debe indicar el precio de la consulta');

      if (!form.consultation.contact_methods.length)
        return onError('Debe indicar el método de contacto');
    }

    if (step === STEP.STEP6) {
      if (!form.bank_account.bank_id)
        return onError('Debe seleccionar el banco');

      if (!form.bank_account.number)
        return onError('Debe indicar la cuenta bancaria');

      if (!form.bank_account.phone_code_id)
        return onError('Debe indicar el código de teléfono');

      if (!form.bank_account.phone)
        return onError('Debe indicar el teléfono');

      if (!form.bank_account.document_type_id)
        return onError('Debe indicar el tipo de documento');

      if (!form.bank_account.document)
        return onError('Debe indicar el documento de identidad');
    }

    if (step === STEP.STEP7) {
      if (!form.description)
        return onError('Debe indicar una descripción');

      if (!form.terms_truth || !form.terms_verify || !form.terms_law)
        return onError('Debe aceptar todos los términos para continuar.');
    }

    return true;
  }

  const parseOutput = () => {
    const name = [form.name, form.lastname || ''].join(' ').trim();

    const consultation: any = Globals.clone(form.consultation);
    consultation.init_hour = form.consultation.init_hour || '';
    consultation.end_hour = form.consultation.end_hour || '';
    consultation.days = (form.consultation.days || []).join(',');

    const productFiles: any = {};
    if (form.products.length) {
      form.products.forEach((item, index) => {
        if (item.image_blob)
          productFiles[`products[${index}][image]`] = item.image;
      });
    }

    return {
      ...form,
      ...productFiles,
      name,
      categories: JSON.stringify(form.categories.map(i => Number(i))),
      languages: JSON.stringify(form.languages.map(i => Number(i))),
      specialties: JSON.stringify(form.specialties),
      areas_specialization: JSON.stringify(form.areas_specialization.map(i => Number(i))),
      animals_affiliate: JSON.stringify(form.animals_affiliate),
      services: JSON.stringify(form.services),
      consultation: JSON.stringify(consultation),
      bank_account: JSON.stringify(form.bank_account),
      products: JSON.stringify(form.products.map(i => ({ name: i.name, image: i.image?.value || i.image || '' }))),
      veterinarians: JSON.stringify(form.veterinarians),
      photo: form.photo?.value || '',
      hasFile: true,
    };
  }

  const onSubmit = async () => {
    try {
      const payload = parseOutput();
      await UserService.admin.createOrUpdate(payload);
      Globals.showSuccess('Su cuenta ha sido creada correctamente');
      nav.replace('/login');

    } catch (error) {
      const err: any = error;
      const message = err?.response?.data?.error || 'Error al crear la cuenta';
      Globals.showError(message);
      console.log(error);
    }
  }

  const prevStep = () => {
    setStep(s => s - 1);
  }

  const nextStep = () => {
    if (!isValidForm()) return;

    if (step < Math.max(...Object.values(STEP)))
      return setStep(s => s + 1);

    onSubmit();
  }

  return {
    form,
    getCities,
    modal,
    nextStep,
    onChange,
    onChangeValue,
    onSubmit,
    prevStep,
    selects,
    setModal,
    step,
  }
}